import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useQuery } from '@tanstack/react-query';

import { AssignmentViewModel, OrganizationStatus, OrganizationType } from 'chaika-frontend-types/frontend-cms';

import { OptionType } from '@components/Select';
import { makeOptionType } from '@utils/makeOptionTypes';
import { organizationQueryOptions } from '@organization/queries/organizationQueries';

interface Props {
    value: AssignmentViewModel[];
    onChange(params: any): void;
}

interface Data {
    selectedOrganizations: AssignmentViewModel[];
    options: OptionType<string>[];
    onDelete(index: number): void;
    addEmptyItem(): void;
    onItemChange(params: Partial<AssignmentViewModel>, index: number): void;
}

export function useOrganizationAssignment({ value = [], onChange }: Props): Data {
    const [selectedOrganizations, setSelectedOrganizations] = useState<AssignmentViewModel[]>(value);

    const { data: clinics = [] } = useQuery(
        organizationQueryOptions.list({ types: OrganizationType.Clinic, statuses: OrganizationStatus.Active }),
    );

    const organizationOptions = makeOptionType(clinics);

    function addEmptyItem(): void {
        onItemChange({ id: uuid() }, selectedOrganizations.length);
    }

    function onItemChange(params: Partial<any>, index: number): void {
        const newState: any[] = [];

        setSelectedOrganizations(prevState => {
            newState.push(...prevState);
            newState[index] = { ...prevState[index], ...params };
            return newState;
        });

        onChange(newState);
    }

    function onDelete(index: number): void {
        const newState: any[] = [];

        setSelectedOrganizations(prevState => {
            newState.push(...prevState.slice(0, index), ...prevState.slice(index + 1, prevState.length));

            return newState;
        });

        onChange(newState);
    }

    return {
        selectedOrganizations,
        addEmptyItem,
        options: organizationOptions,
        onDelete,
        onItemChange,
    };
}
